import { FeatureCollection, Geometry, Polygon } from 'geojson';

import { UnitEnum } from './types/units';

export type ProjectOverviewItem = {
  body: string;
  images: { name: string; caption: string; url: string }[];
};

export type SpeciesGroupId = `g${number}`;

export enum ConservationMethodology {
  vnu = 'vnu', // Verifiable Nature Unit
  bu = 'bu', // Biodiversity Unit
}

export type Project = {
  id: string;
  natureUnitVariant: ConservationMethodology;
  name: string;
  location_description: string;
  biomes: string[];
  organization: {
    id: string;
    name: string;
  };
  area: number;
  areaUnits: UnitEnum;
  ecoregions: string[];
  speciesGroups: Record<SpeciesGroupId, string>;
  overview: {
    about: ProjectOverviewItem;
    species: ProjectOverviewItem;
    community: ProjectOverviewItem;
  };
  subtype: 'restoration' | 'conservation';
};

export type CellProperties = {
  id?: string;
  name: string;
  area: number;
  area_cut: number;
  cut: boolean;
  parent_id: string;
  threats_ts: string;
  threats: number;
  threats_i: number;
  threats_i_ts: Record<string, number>;
  built_ts: string;
  built: number;
  crop_ts: string;
  crop: number;
  edge_ts: string;
  edge: number;
  mine_ts: string;
  mine: number;
  road_ts: string;
  road: number;
  treeloss_ts: string;
  treeloss: number;
  index: number;
  freshwater_habitat: number;
  [speciesGroupId: SpeciesGroupId]: number;
  indicator_species_score: number;
  ecological_integrity: number;
  threats_i_change: number;
  threats_i_change_percentage: number;
  // TODO: MVP-5183 decide contract with science on fact names
  ecological_integrity_ts: number;
  area_units: UnitEnum;
};

export type TilesetsByLayer = {
  [layer: string]: {
    [year: string]: Tileset;
  };
};

export type Tileset = {
  id: string;
  sourceLayer: string;
  type?: 'vector' | 'raster';
};

export type ConservationProject = {
  id: string;
  natureUnitVariant: ConservationMethodology;
  name: string;
  location_description: string;
  biomes: string[];
  organization: {
    id: string;
    name: string;
  };
  organization_id: string;
  ecoregions: string[];
  area: number;
  areaUnits: UnitEnum;
  subtype: 'restoration' | 'conservation';
  speciesGroups: Record<SpeciesGroupId, string>;
  overview: {
    about: ProjectOverviewItem;
    species: ProjectOverviewItem;
    community: ProjectOverviewItem;
  };
};

export type PolygonsGeojson = FeatureCollection<
  Polygon,
  {
    Name: string;
    area_km2: number;
  }
>;

export type CellsGeojson = FeatureCollection<Geometry, CellProperties>;

export type Observation = {
  id: string;
  species: string;
  key: number;
  year: number;
  decimalLatitude: number;
  decimalLongitude: number;
  geometry: string;
  references?: string;
  image_url?: string;
  source: string;
  eventDate?: string;
  indicator_group: SpeciesGroupId;
  species_common_name?: string;
  groups_detected?: number;
  groups_total?: number;
};

export enum Threat {
  built = 'built',
  treeloss = 'treeloss',
  crop = 'crop',
  road = 'road',
  mine = 'mine',
  overgrazing = 'overgrazing',
  other = 'other',
  airstripes = 'airstripes',
  minor_road = 'minor_road',
  major_road = 'major_road',
  fire = 'fire',
  edge = 'edge',
  river = 'river',
}

export type FactType =
  | 'r1_conservation_current_year'
  | 'r1_conservation_baseline_year'
  | 'r1_conservation_no_nature_units'
  | 'r1_conservation_ecological_integrity'
  | 'r1_conservation_ecological_integrity_baseline'
  | 'r1_conservation_ecological_integrity_lowest'
  | 'r1_conservation_ecological_integrity_highest'
  | 'r1_conservation_ecological_integrity_ts'
  | 'r1_conservation_ecological_integrity_change_percentage'
  | 'r1_conservation_habitat_intactness_change_percentage'
  | 'r1_conservation_threats_change_percentage'
  | 'r1_conservation_indicator_species_change_percentage'
  | 'r1_conservation_species_group_presence'
  | 'r1_conservation_species_group_presence_ts'
  | 'r1_conservation_degradation_drivers_i_baseline'
  | 'r1_conservation_degradation_drivers_i'
  | 'r1_conservation_threats_i'
  | 'r1_conservation_threats_i_baseline'
  | 'r1_conservation_threats_baseline_year'
  | 'r1_conservation_habitat_intactness_baseline_year'
  | 'r1_conservation_indicator_species_baseline_year'
  | 'r1_conservation_ecological_integrity_baseline_year'
  | 'r1_conservation_threats_i_ts'
  | 'r1_conservation_threats_ts'
  | 'r1_conservation_threats_i_1_year_change'
  | 'r1_conservation_indicator_species_score'
  | 'r1_conservation_indicator_species_baseline_year_score'
  | 'r1_conservation_indicator_species_score_1_year_change'
  | 'r1_conservation_indicator_species_score_baseline_year'
  | 'r1_conservation_indicator_species_ts'
  | 'r1_conservation_groups_detected'
  | 'r1_conservation_groups_total'
  | 'r1_conservation_drivers_total'
  | 'r1_conservation_drivers_detected'
  | 'r1_conservation_species_pct_historic_graph'
  | 'r1_conservation_habitat_intactness_historic_index_graph'
  | 'r1_conservation_habitat_intactness_historic_graph'
  | 'r1_conservation_detected_threats_breakdown'
  | 'r1_conservation_built'
  | 'r1_conservation_built_ts'
  | 'r1_conservation_river'
  | 'r1_conservation_river_ts'
  | 'r1_conservation_crop'
  | 'r1_conservation_crop_ts'
  | 'r1_conservation_edge'
  | 'r1_conservation_edge_ts'
  | 'r1_conservation_mine'
  | 'r1_conservation_mine_ts'
  | 'r1_conservation_road'
  | 'r1_conservation_road_ts'
  | 'r1_conservation_treeloss'
  | 'r1_conservation_treeloss_ts'
  | 'r1_conservation_fire'
  | 'r1_conservation_fire_ts'
  | 'r1_conservation_habitat_intactness_historic_explainer'
  | 'r1_conservation_degradation_drivers_historic_explainer'
  | 'r1_conservation_indicator_species_historic_explainer'
  | 'r1_conservation_ecological_integrity_historic_explainer'
  | 'r1_conservation_ecological_water_historic_explainer'
  | 'r1_water_holding_capacity_per_ha'
  | 'r1_water_holding_capacity_latest_analysis_per_ha'
  | 'r1_water_holding_capacity_latest_analysis_total'
  | 'r1_historic_water_and_soil_moisture_graph'
  | 'r1_historic_water_and_soil_moisture_per_ha_graph'
  | 'r1_soil_moisture_latest_analysis_total'
  | 'r1_soil_moisture_latest_analysis_per_ha'
  | 'r1_above_ground_carbon_i'
  | 'r1_above_ground_carbon_baseline'
  | 'r1_above_ground_carbon_pct_change'
  | 'r1_conservation_ndvi_baseline_year'
  | 'r1_conservation_ndvi_i'
  | 'r1_conservation_ndvi_baseline'
  | 'r1_conservation_ndvi_change_percentage'
  | 'r1_conservation_ndvi_ts'
  | 'r1_conservation_ndvi_historic_explainer'
  | 'r1_conservation_ndvi_legend_range'
  | 'r1_conservation_habitat_intactness_legend_color_range'
  | 'r1_conservation_habitat_intactness_data_range'
  | 'r1_conservation_indicator_species_data_range'
  | 'r1_conservation_ecological_integrity_data_range'
  | 'r1_conservation_species_occupancy_ts'
  | 'r1_conservation_species_occupancy_score'
  | 'r1_conservation_species_occupancy_change_percentage'
  | 'r1_conservation_species_occupancy_baseline_year'
  | 'r1_conservation_species_occupancy_historic_explainer'
  | 'r1_conservation_species_detected'
  | 'r1_conservation_habitat_intactness_static_map_explainer'
  | 'r1_conservation_built_area_pct'
  | 'r1_conservation_treeloss_area_pct'
  | 'r1_conservation_crop_area_pct'
  | 'r1_conservation_road_area_pct'
  | 'r1_conservation_mine_area_pct'
  | 'r1_conservation_overgrazing_area_pct'
  | 'r1_conservation_other_area_pct'
  | 'r1_conservation_airstripes_area_pct'
  | 'r1_conservation_minor_road_area_pct'
  | 'r1_conservation_major_road_area_pct'
  | 'r1_conservation_fire_area_pct'
  | 'r1_conservation_edge_area_pct';

export type Fact<N extends FactType = FactType, V = unknown> = {
  name: N;
  value: V;
  unit: UnitEnum;
  measured_at?: string | null;
  labels?: ConservationFactLabel[];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FactLike<N = any, V = any> = {
  name: N;
  value: V;
  unit: UnitEnum;
  measured_at?: string | null;
};

export type Dashboard = {
  facts: Fact[];
};

export enum TrendLabel {
  increasing = 'increasing',
  stable = 'stable',
  decreasing = 'decreasing',
}

export enum ConservationBenchmarkType {
  thriving = 'thriving',
  healthy = 'healthy',
  managing = 'managing',
  fragile = 'fragile',
  degraded = 'degraded',
}

export enum ConservationFactLabelNameEnum {
  significance = 'significance',
}

export type ConservationFactLabel<T = number | string | null | unknown> = {
  name: ConservationFactLabelNameEnum;
  value: T;
};

export const CONSERVATION_PROJECTS = {
  MAJETE: 'majete',
  MAJETE_V2: 'majete_v2',
  DABCHICK: 'dabchick',
  ODZALA: 'odzala',
  ODZALA_V2: 'odzala_v2',
  MALE: 'male',
  CAMTAUA: 'camtaua',
  ZAKOUMA: 'zakouma',
  GARAMBA: 'garamba',
  UPEMBA: 'upemba',
} as const;

export const MIXED_METHODOLGY_PROJECTS = [
  // Projects that use the BU dashboards, but issue VNUs
  CONSERVATION_PROJECTS.GARAMBA,
  CONSERVATION_PROJECTS.ZAKOUMA,
  CONSERVATION_PROJECTS.ODZALA_V2,
  CONSERVATION_PROJECTS.MAJETE_V2,
] as const;

export type ConservationProjectName = (typeof CONSERVATION_PROJECTS)[keyof typeof CONSERVATION_PROJECTS];

export const CONSERVATION_PROJECTS_WITH_STATIC_PICTURES: readonly ConservationProjectName[] = [
  CONSERVATION_PROJECTS.MALE,
] as const;

export const CONSERVATION_MAP_URL = {
  [CONSERVATION_PROJECTS.MAJETE]: 'mapbox://',
  [CONSERVATION_PROJECTS.MAJETE_V2]: 'https://mapserver.landler.io/geoserver/majete/',
  [CONSERVATION_PROJECTS.DABCHICK]: 'mapbox://',
  [CONSERVATION_PROJECTS.ODZALA]: 'mapbox://',
  [CONSERVATION_PROJECTS.MALE]: 'mapbox://',
  [CONSERVATION_PROJECTS.CAMTAUA]: 'https://mapserver.landler.io/geoserver/camtaua/',
  [CONSERVATION_PROJECTS.ZAKOUMA]: 'https://mapserver.landler.io/geoserver/zakouma/',
  [CONSERVATION_PROJECTS.GARAMBA]: 'https://mapserver.landler.io/geoserver/garamba/',
  [CONSERVATION_PROJECTS.UPEMBA]: 'https://mapserver.landler.io/geoserver/upemba/',
  [CONSERVATION_PROJECTS.ODZALA_V2]: 'https://mapserver.landler.io/geoserver/odzala_v2/',
};

/**
 * GET /api/v1/conservation/{project_name}/
 */
export const GET_CONSERVATION_FILES_PATH = 'conservation/{project_name}';
export type GetConservationFilesRequestPathVariables = { project_name: string };
export type GetConservationFilesRequestQueryParams = undefined;
export type GetConservationFilesSuccessDto = {
  'cells.json': CellsGeojson;
  'cells_2019.json': CellsGeojson;
  'cells_2020.json': CellsGeojson;
  'cells_2021.json': CellsGeojson;
  'cells_2022.json': CellsGeojson;
  'cells_2023.json': CellsGeojson;
  'dashboard.json': Dashboard;
  'observation.json': Observation[];
  'polygon.json': PolygonsGeojson;
  'project.json': ConservationProject;
  'tilesets.json': TilesetsByLayer;
} & {
  [K in `${SpeciesGroupId}_observation.json`]?: Observation[];
} & {
  [K in `threat_${Threat}.json`]?: CellsGeojson;
};
export type GetConservationFilesErrorDto = Error;
